// CRM / Module 5 entity shapes for ai-quote 2.0.
//
// Canonical source: spec/14-module-crm.md + spec/20-data-model.md.
// Field names and enum string values mirror the spec EXACTLY — UI agents,
// migration scripts, and PDF exporters all key off these names. Don't rename
// without coordinating with the spec.
//
// Conventions (from spec/20):
//   - IDs are strings (Firebase push keys for now; UUIDv7 noted as future)
//   - All entities extend BaselineFields (audit + soft-delete + versioning)
//   - Snapshots paired with FK (customerId + customerNameSnapshot)
//   - Date fields: *At for timestamps, *Date for ISO dates (both ISO 8601 strings)
//   - Status enums: lowercase snake_case
//
// NOTE: Parts are TOP-LEVEL entities at /parts/{partId}. Quotes reference parts
// via partIds[] and layer per-quote specs via partQuotedSpecs. See spec/14.

// =====================================================
// Baseline (every persisted entity has these)
// =====================================================

/** Single audit-trail entry. Auto-populated by the data-access layer. */
export interface AuditEntry {
  ts: string; // ISO 8601
  user: string; // userId or 'system'
  action:
    | 'create'
    | 'update'
    | 'delete'
    | 'restore'
    | 'status_change'
    | 'approve'
    | 'reject'
    | 'merge'
    | 'split'
    | 'tier_change'
    | string; // module-specific actions allowed
  fields?: Record<string, { from: unknown; to: unknown }>;
  metadata?: unknown;
  source: 'ui' | 'worker' | 'webhook' | 'migration' | 'brain' | 'qb_sync';
}

/**
 * Fields every persisted entity carries. Per spec/20, the data-access layer
 * auto-populates these — module callers never set them directly.
 */
export interface BaselineFields {
  id: string;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  createdBy?: string; // userId — TODO wire up once auth role flow lands
  updatedBy?: string; // userId — TODO wire up once auth role flow lands
  _v: number; // schema version
  _audit: AuditEntry[];
  _deleted?: boolean; // soft delete (default false)
  _deletedAt?: string;
  _deletedBy?: string;
  _deletedReason?: string;
}

// =====================================================
// Shared atoms
// =====================================================

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface Contact {
  id: string;
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  primary: boolean;
  notes?: string;
}

/** Per spec/14, customer.notes is a running log of {ts, user, text} entries. */
export interface CustomerNoteEntry {
  ts: string; // ISO 8601
  user: string; // userId
  text: string;
}

// =====================================================
// Customer
// =====================================================

export type CustomerTier = 'vip' | 'good' | 'slow_pay' | 'bad' | 'new';

export type CustomerTerms = 'Net 30' | 'Net 15' | 'COD' | 'Prepaid' | 'Custom';

/**
 * Computed grade — refreshed by Module 7 analytics; Module 5 only reads it.
 * Per spec/14 "Auto-grading rules".
 */
export interface CustomerGrade {
  score: number; // 0-100
  paymentTimelinessAvg?: number; // days (negative = early)
  repeatBusinessCount?: number;
  lifetimeRevenue?: number; // dollars
  lifetimeMargin?: number; // dollars
  averageJobSize?: number; // dollars
  riskFlags?: string[]; // e.g. 'slow_pay_pattern', 'dispute_history'
}

export interface Customer extends BaselineFields {
  code: string; // 3-4 char auto-distilled (see v1:21469)
  companyName: string;
  contacts: Contact[];
  billingAddress: Address;
  shippingAddress?: Address; // may be same as billing
  terms: CustomerTerms;
  taxExempt: boolean;
  taxExemptCert?: string; // ref to document in Module 2
  rep?: string; // KMF salesperson code ('JMJ' etc)
  tier: CustomerTier;
  tierManualOverride: boolean; // true = locked by Justin
  tierLastReviewed?: string; // ISO date
  grade?: CustomerGrade; // computed by M7
  notes?: CustomerNoteEntry[];
  oneDriveFolderId?: string; // link to Module 2 storage
  lastContactDate?: string; // ISO date
  nextFollowUpDate?: string; // ISO date
}

// =====================================================
// Part (top-level, /parts/{partId})
// =====================================================

export type PartType = 'machined' | 'cut_blank' | 'weldment' | 'hybrid' | 'purchased' | 'collar' | 'turninsulation';

export type MaterialSnapshotSource =
  | 'last_paid'
  | 'vendor_quote'
  | 'mcmaster_api'
  | 'manual';

export interface MaterialCost {
  perPiece?: number[]; // per-tier prices (v1 shape preserved)
  perBar?: number;
  perSheet?: number;
  vendorId?: string;
  vendorPartNum?: string;
  vendorUrl?: string;
  isComponent?: boolean;
  packSize?: number;
  perPack?: number;
  supplierTaxPct?: number;
  snapshotAt?: string; // ISO date — locked-in price at quote time
  snapshotSource?: MaterialSnapshotSource;
  /** Temporary placeholder price set in the MaterialStep picker before an
   *  actual vendor quote comes back. NOT customer-quoteable on its own —
   *  the Mark Sent flow on QuoteDetailPage checks `ballparkSet` and prompts
   *  the user to confirm before allowing the quote to leave Draft. The
   *  pricing engine wire-up (folding this into per-piece cost) is a separate
   *  slice; for now this is just a persisted hint + warning. */
  ballparkPrice?: number;
  /** True iff a ballpark price was deliberately set on this part (so the
   *  Mark Sent flow can distinguish "no price yet" from "user set a ballpark"). */
  ballparkSet?: boolean;
  /** Free-text note attached to a ballpark price — e.g. "based on last 1/4 G-10
   *  job, waiting on Alro requote". Persisted alongside ballparkPrice. */
  ballparkNote?: string;
}

export interface MachineSetup {
  machine: string;
  hrsPerTier: number[];
  setupHrs: number;
}

export interface DxfFileEntry {
  filename: string;
  vendor?: string;
  setupCost?: number;
  perPieceCost?: number;
  supplierTaxPct?: number;
}

export interface OutsideService {
  /** Legacy freeform service-type label (e.g. "Anodize"). Kept for backwards
   *  compatibility with v1 data + existing rows; new code should use
   *  `serviceType` (the typed taxonomy) below. */
  type: string;
  /** Preferred — references the Vendor entity by id. Old `vendor` string is a fallback. */
  vendorId?: string;
  /** Legacy freeform vendor name. Kept for backwards compatibility; new code
   *  should set `vendorId` and look up the Vendor record via getVendor(). */
  vendor?: string;
  /** Preferred — references the VendorServiceType taxonomy. Maps to a label
   *  in VENDOR_SERVICE_LABELS. When 'other', `type` carries the freeform name. */
  serviceType?: VendorServiceType;
  setupCost?: number;
  perPieceCost?: number;
  supplierTaxPct?: number;
  csmHandlingFeePct?: number;
  /** Mode determines how the cost rolls into pricing. */
  costMode?: 'per_piece' | 'lot_charge';
  /** Flat lot charge $. When costMode === 'lot_charge', this is the per-batch
   *  cost regardless of qty. The legacy setupCost + perPieceCost fields stay
   *  for older parts. */
  lotCharge?: number;
}

/** Tooling — v1 shape preserved as freeform until Module 3 ships its tool entity. */
export interface ToolingEntry {
  [k: string]: unknown;
}

export interface WeldOp {
  method: string;
  hrs: number;
  electrodeType?: string;
  fillerCost?: number;
  // Optional structured fields — replace freeform method/electrode/filler
  // when these are set. Old strings stay for legacy data.
  methodStructured?: WeldMethod;
  filler?: string;          // from WELD_FILLERS_BY_METHOD[methodStructured]
  diameter?: WireDiameter;
}

/** Only set when part.type === 'weldment'. Sub-parts are nested Parts. */
export interface WeldmentSpec {
  subParts: Part[];
  weldOps: WeldOp[];
}

/** Only set when part.type === 'purchased'. */
export interface PurchasedSource {
  vendor: 'mcmaster' | 'misumi' | 'grainger' | 'other';
  sku?: string;
  url?: string;
  leadTime?: string;
  markupPct?: number;
}

/**
 * Only set when part.type === 'collar' (display label: "Sheet Material").
 * Originally just a picture-frame rectangle cut from G-10 / GPO-3 / G-11
 * phenolic sheet; now covers four shape variants — picture frame, plain
 * rectangle, circle, and C-shape standoff — all sharing the same nesting +
 * cut-time math layer. All dimensions in inches; times in minutes; rates in
 * IPM / $/hr.
 *
 * Optional fields fall back to `COLLAR_DEFAULTS` (src/shared/collarMath.ts)
 * when reading on the wizard. The enum value `'collar'` is preserved for
 * backwards compatibility with existing data — see PartTypeBadge.tsx for the
 * "Sheet Material" rename.
 */
export interface CollarSpec {
  /** Outer rectangle length, inches. */
  outsideLen: number;
  /** Outer rectangle width, inches. */
  outsideWid: number;
  /** Picture-frame border width; inner cutout = outer minus 2 × this. */
  frameWidth: number;
  /** Sheet stock length, inches. Default from COLLAR_DEFAULTS. */
  sheetLen?: number;
  /** Sheet stock width, inches. Default from COLLAR_DEFAULTS. */
  sheetWid?: number;
  /** Inter-part spacing on the nest, inches. v1 called this `kerf`. */
  spacing?: number;
  /** Feed rate, IPM. Default from COLLAR_DEFAULTS. */
  feedIpm?: number;
  /** Depth of cut per pass, inches. Default from COLLAR_DEFAULTS. */
  doc?: number;
  /** Load/unload time per sheet, minutes. Default from COLLAR_DEFAULTS. */
  loadUnloadMin?: number;
  /** Screw-down-to-table time per sheet, minutes. Default from COLLAR_DEFAULTS. */
  screwDownMin?: number;
  /** Burdened machine rate, $/hr. Default from COLLAR_DEFAULTS. */
  hourlyRate?: number;
  /** Per-sheet material price, $. */
  sheetPrice?: number;
  /** UI preference: show times in hours instead of minutes. */
  timeUnitHours?: boolean;
  // ─── Shape picker (additive — frame keeps the legacy behavior) ────────
  /** Which of the four sheet-material shapes this part is. Defaults to 'frame'
   *  when unset so existing collar records keep their picture-frame behavior. */
  shape?: 'frame' | 'rectangle' | 'circle' | 'standoff';
  /** Inputs specific to the standoff shape. legLen × height is treated as the
   *  bounding-box footprint for nesting (the C-shape's enclosing rectangle). */
  standoff?: {
    legLen: number;
    height: number;
    thickness: number;
  };
  /** Circle diameter, inches. Only meaningful when shape === 'circle'. For
   *  nesting we over-estimate by treating the disk as a square with side =
   *  diameter — see CollarNestingStep for the documented compromise. */
  diameter?: number;
}

/**
 * Only set when part.type === 'turninsulation'. KMF electric-motor heritage —
 * B-stage epoxy-cured insulation tape between coil turns. v1 carried this as a
 * placeholder ("calculator coming") and so do we; the math layer is a future
 * pass. All fields optional so the wizard can save a stub.
 */
export interface TurnInsulationSpec {
  /** Length of tape per turn, inches. Placeholder for the future calculator. */
  tapeLengthPerTurn?: number;
  /** Number of turns per coil. */
  turnsPerCoil?: number;
  /** Number of coils. */
  coilCount?: number;
  /** Notes on B-stage curing schedule, manufacturer prep, etc. */
  curingNotes?: string;
}

export interface Part extends BaselineFields {
  number: string; // e.g. 'BRK-2245-A'
  description: string;
  type: PartType;
  qtyTiers: number[]; // per-part tiers (may override quote-level)
  materialCost?: MaterialCost;
  setupCost?: number; // one-time programming/setup
  setupHrs?: number; // per-machine setup time
  machines?: MachineSetup[];
  dxfFiles?: DxfFileEntry[];
  outsideServices?: OutsideService[];
  tooling?: ToolingEntry[];
  weldment?: WeldmentSpec; // only when type === 'weldment'
  purchasedSource?: PurchasedSource; // only when type === 'purchased'
  collar?: CollarSpec; // only when type === 'collar'
  turnInsulationSpec?: TurnInsulationSpec; // only when type === 'turninsulation'
  attachedDrawings?: string[]; // documentId FKs (Module 2)
  /** Optional explicit thumbnail URL. When set, PartCardImage and the
   *  PartDrawingThumbnails grid render this instead of auto-picking from
   *  attachedDrawings. Useful when the canonical drawing is a CAD file
   *  (STEP/SLDPRT) that we can't preview natively but Justin has a
   *  separate PNG/JPG thumbnail for. */
  thumbnailUrl?: string;
  notes?: string;
  /** True when customer brings their own material — shop charges handling fee
   *  instead of marking up cost. v1 called this CSM. */
  customerSuppliedMaterial?: boolean;
  /** Handling fee charged when CSM is true. $ per piece (per-tier defaults
   *  to a single value; UI may extend to per-tier later). */
  csmHandlingFeePerPiece?: number;
  /** FK into the /materials library — set by the MaterialStep picker when the
   *  user picks a Material record. Optional / additive: legacy parts and
   *  freeform-typed material entries still work via `materialCost.vendorPartNum`.
   *  When set, the picker drives `materialCost.vendorId` to the material's
   *  primary vendor and surfaces last-paid + thickness comparisons. */
  materialId?: string;
}

// =====================================================
// Quote
// =====================================================

export type JobType =
  | 'multi_part_production'
  | 'prototype'
  | 'one_off'
  | 'quick_quote'
  | 'spot_quote'
  | 'welding_job'
  | 'machine_build';

export type QuoteStatus =
  | 'lead'
  | 'draft'
  | 'sent'
  | 'follow_up'
  | 'negotiating'
  | 'won'
  | 'lost'
  | 'expired'
  | 'imported';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'na';

export type FollowUpStage = 'day_3' | 'day_7' | 'day_14' | 'paused' | 'done';

/**
 * Per-part snapshot at quote time. Indexed by partId.
 * Lets the quote carry "what we promised for THIS part at THIS quote" even if
 * the canonical Part record drifts later.
 */
export interface PartQuotedSpec {
  qtyTiers: number[];
  estimatedSetupHrs: number;
  estimatedRunHrs: number;
  materialCostSnapshot: number;
  pricePerTier: number[];
}

export interface QuoteRevision {
  rev: number;
  ts: string; // ISO 8601
  user: string; // userId
  snapshot: Record<string, unknown>; // whole-quote snapshot
  changeNotes: string;
}

export interface FollowUpDraftHistoryEntry {
  ts: string;
  draftId: string;
  stage: FollowUpStage;
  sent: boolean;
  sentAt?: string;
}

export interface QuoteFollowUp {
  nextDueDate?: string; // ISO date — when to draft next nudge
  stage: FollowUpStage;
  draftsHistory: FollowUpDraftHistoryEntry[];
  paused: boolean;
  pauseReason?: string;
}

export interface QuoteOutcome {
  decision: 'won' | 'lost';
  decidedAt: string; // ISO date
  reason?: string;
  competitor?: string;
  competitorPrice?: number;
  lostNotes?: string;
}

export interface Quote extends BaselineFields {
  number: string; // 'Q-1042' — human-readable, sequential
  customerId: string; // FK to customer
  customerNameSnapshot: string; // frozen at time of quote
  rfqRef?: string; // customer's RFQ ref number
  rfqEmailId?: string; // FK to source email (Module 2/Outlook)
  dueDate?: string; // ISO date — customer's needed-by
  jobType: JobType;
  partIds: string[]; // FK to /parts/{partId}
  partQuotedSpecs: Record<string, PartQuotedSpec>;
  qtyTiers: number[]; // top-level tiers (parts can override)
  status: QuoteStatus;
  imported?: boolean; // true = bootstrap historical
  importedTotal?: number; // honored when imported=true
  totalsByTier?: number[]; // computed
  marginsByTier?: number[]; // computed
  marginWarning?: boolean; // true if any tier margin < state.ui.minMarginPct
  approvalRequired?: boolean; // auto-set if total > state.ui.approvalThreshold
  approvalStatus?: ApprovalStatus;
  approvedBy?: string;
  approvedAt?: string;
  revisions?: QuoteRevision[];
  followUp?: QuoteFollowUp;
  sentAt?: string;
  sentByEmailId?: string; // FK to sent email in Outlook
  pdfUrl?: string; // OneDrive link to generated PDF
  outcome?: QuoteOutcome;
  fromTemplateId?: string;
  templateRefName?: string;
  /** URLs for drawings / attachments that came in with the RFQ, or were
   *  dragged onto the quote builder. Pool that per-part attachedDrawings
   *  can be picked from. URLs come from Firebase Storage uploads via
   *  drawingStorage (uploadRfqAttachment for RFQ-pool uploads). */
  attachedDrawings?: string[];
  /** ID of the RFQ this quote was generated from (if any). For trace.
   *  Currently a UUID minted client-side at extraction time; future
   *  RFQ-intake pipeline will own canonical IDs at /rfqs/{rfqId}. */
  fromRfqId?: string;
}

// =====================================================
// Order
// =====================================================

export type OrderStatus = 'draft' | 'in_production' | 'shipped' | 'closed' | 'void';

export interface Order extends BaselineFields {
  number: string; // 'O-2245'
  customerId: string;
  customerNameSnapshot: string;
  fromQuoteId?: string; // FK back to quote (may be null for direct orders)
  status: OrderStatus;
  statusChangedAt?: string; // ISO 8601 — last status transition
  totalSnapshot: number; // frozen at order creation
  qtyOrderedByTier?: number[];
  poNumber?: string; // customer's PO #
  poReceivedAt?: string;
  poDocumentId?: string; // FK to Module 2 doc
  promisedShipDate?: string;
  actualShipDate?: string;
  trackingNumber?: string;
  jobIds?: string[]; // FK to Module 4 jobs
  invoiceIds?: string[]; // FK to Module 7 AR invoices
  imported?: boolean;
  importedRawStatus?: string; // v1's JobBOSS legacy status
}

// =====================================================
// QuoteTemplate
// =====================================================

/**
 * Reusable quote skeleton. customerId optional — when set, the template is
 * only suggested for that customer (per spec/14 "Quote templates" section).
 * Stored at /quoteTemplates/{id}.
 */
export interface QuoteTemplate extends BaselineFields {
  name: string;
  description?: string;
  customerId?: string; // when set, customer-specific template
  parts: Part[]; // full nested part structure
  qtyTiers: number[];
  jobType: JobType;
  notes?: string;
}

// =====================================================
// Vendor — outside-service / material suppliers
// =====================================================

export type VendorServiceType =
  | 'laser_cutting'
  | 'plasma_cutting'
  | 'waterjet_cutting'
  | 'painting'
  | 'powder_coating'
  | 'anodizing'
  | 'black_oxide'
  | 'heat_treat'
  | 'grinding'
  | 'broaching'
  | 'edm'
  | 'plating_zinc'
  | 'plating_nickel'
  | 'plating_chrome'
  | 'passivation'
  | 'deburring'
  | 'polishing'
  | 'tumbling'
  | 'silk_screen'
  | 'engraving'
  | 'material_supply'
  | 'other';

export const VENDOR_SERVICE_LABELS: Record<VendorServiceType, string> = {
  laser_cutting: 'Laser cutting',
  plasma_cutting: 'Plasma cutting',
  waterjet_cutting: 'Waterjet cutting',
  painting: 'Painting',
  powder_coating: 'Powder coating',
  anodizing: 'Anodizing',
  black_oxide: 'Black oxide',
  heat_treat: 'Heat treat',
  grinding: 'Grinding',
  broaching: 'Broaching',
  edm: 'EDM',
  plating_zinc: 'Plating — zinc',
  plating_nickel: 'Plating — nickel',
  plating_chrome: 'Plating — chrome',
  passivation: 'Passivation',
  deburring: 'Deburring',
  polishing: 'Polishing',
  tumbling: 'Tumbling',
  silk_screen: 'Silk-screen',
  engraving: 'Engraving',
  material_supply: 'Material supply',
  other: 'Other',
};

export interface Vendor extends BaselineFields {
  name: string;                          // "Cleveland Laser Cutters"
  services: VendorServiceType[];         // what they do
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: Address;
  leadTimeDays?: number;                 // typical turnaround
  /** Default markup % we apply to their price. 0 = pass-through. */
  defaultMarkupPct?: number;
  /** Default tax pct (vendor surcharge). */
  defaultTaxPct?: number;
  notes?: string;
  favorite?: boolean;                    // surface at top of pickers
  /** True if imported from external source (e.g. v1 QuickBooks dump). */
  importedFromV1?: boolean;
  /** Freeform tags — Justin types them. First tag drives default grouping in
   *  the Settings UI. Multiple tags per item allowed (e.g. ['mill','5-axis']). */
  tags?: string[];
  /** Manual ordering within the same tag group. Ascending. New items get
   *  (max existing in group + 100) on create so they land at the bottom. */
  sortOrder?: number;
}

// =====================================================
// Machine — shop machine with hourly rates
// =====================================================

export interface Machine extends BaselineFields {
  name: string;                   // 'Doosan DNM 750', 'GR Haas', etc.
  /** $ / hour while actively cutting. */
  runRate: number;
  /** $ / hour during setup (often equal to runRate; modeled separately so
   *  the shop can differentiate as the cost model matures). */
  setupRate: number;
  /** Default % markup applied to runtime + setup cost (overrides quote-level). */
  defaultMarkupPct?: number;
  /** Catalog metadata. */
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  /** Active in the shop right now. False = soft-disable from pickers without
   *  losing historical references on existing quotes. */
  active?: boolean;
  notes?: string;
  /** True if imported from v1 export. */
  importedFromV1?: boolean;
  /** Freeform tags — Justin types them. First tag drives default grouping in
   *  the Settings UI. Multiple tags per item allowed (e.g. ['mill','5-axis']). */
  tags?: string[];
  /** Manual ordering within the same tag group. Ascending. New items get
   *  (max existing in group + 100) on create so they land at the bottom. */
  sortOrder?: number;
}

// =====================================================
// Material — stock library with vendor linkage
// =====================================================

export type MaterialCategory =
  | 'aluminum' | 'steel' | 'stainless' | 'brass' | 'copper' | 'bronze'
  | 'titanium' | 'phenolic' | 'plastic' | 'rubber' | 'composite' | 'other';

export const MATERIAL_CATEGORY_LABELS: Record<MaterialCategory, string> = {
  aluminum: 'Aluminum', steel: 'Steel', stainless: 'Stainless',
  brass: 'Brass', copper: 'Copper', bronze: 'Bronze',
  titanium: 'Titanium', phenolic: 'Phenolic (G-10 / GPO-3 / G-11)',
  plastic: 'Plastic', rubber: 'Rubber', composite: 'Composite', other: 'Other',
};

/**
 * Tailwind class strings per material category for badges + group headers.
 * Picks colors that read intuitively — steel = dark/iron, aluminum = silver,
 * stainless = blueish, brass = gold/amber, copper = orange, phenolic = green
 * (G-10 is famously olive-green), plastic = purple, etc. All sourced from
 * the project palette (tailwind.config.ts).
 *
 * Each entry is a self-contained pill class string — use as
 *   <span className={MATERIAL_CATEGORY_COLORS[cat]}>…</span>
 *
 * The 'border' / 'text' tokens stay aligned with the badge convention used
 * by PartTypeBadge so the family of badges across the app reads consistently.
 */
export const MATERIAL_CATEGORY_COLORS: Record<MaterialCategory, string> = {
  aluminum:  'bg-steel/15 text-steel border border-steel/30',
  steel:     'bg-navy/15 text-navy border border-navy/30',
  stainless: 'bg-blue/15 text-blue border border-blue/30',
  brass:     'bg-amber/15 text-amber border border-amber/40',
  copper:    'bg-orange/15 text-orange border border-orange/30',
  bronze:    'bg-amber/20 text-amber border border-amber/50',
  titanium:  'bg-purple/15 text-purple border border-purple/30',
  phenolic:  'bg-green/15 text-green border border-green/30',
  plastic:   'bg-purple/10 text-purple border border-purple/25',
  rubber:    'bg-text-mute/15 text-text-mute border border-line-dark',
  composite: 'bg-teal/15 text-teal border border-teal/30',
  other:     'bg-bg text-text-mute border border-line-dark',
};

export type MaterialForm =
  | 'bar' | 'round' | 'plate' | 'sheet' | 'tube' | 'block' | 'wire' | 'other';

export interface MaterialPriceSnapshot {
  /** Price value in dollars, in the unit specified. */
  price: number;
  unit: 'per_lb' | 'per_piece' | 'per_bar' | 'per_sheet' | 'per_pack' | 'per_ft';
  /** Vendor we paid this price to. Optional — sometimes you only have the price. */
  vendorId?: string;
  /** ISO date when this price was paid. */
  date: string;
  /** PO number or receipt reference. */
  reference?: string;
}

export interface Material extends BaselineFields {
  /** Human label, e.g. '6061-T6 Aluminum'. */
  name: string;
  category: MaterialCategory;
  form?: MaterialForm;
  /** Common spec — e.g. '1/4 plate', '1.5x1.5 bar', '24×48 sheet'. Free-text. */
  spec?: string;
  /** lb / in³ — for cost-by-weight calc when applicable. */
  density?: number;
  /** Vendor IDs who supply this material. Primary first. References Vendor.id. */
  vendorIds: string[];
  /** Most recent paid price snapshot. Hand-edited or auto-set from purchasing. */
  lastPaid?: MaterialPriceSnapshot;
  /** Free-form notes — finish, callout, datasheet link, etc. */
  notes?: string;
  /** False = soft-disable from pickers. Default true. */
  active?: boolean;
  /** True if imported from v1 export. */
  importedFromV1?: boolean;
  /** Freeform tags — Justin types them. First tag drives default grouping in
   *  the Settings UI. Multiple tags per item allowed (e.g. ['mill','5-axis']). */
  tags?: string[];
  /** Manual ordering within the same tag group. Ascending. New items get
   *  (max existing in group + 100) on create so they land at the bottom. */
  sortOrder?: number;
}

// =====================================================
// Weld op taxonomies — structured dropdowns for WeldOpsStep
// =====================================================

export const WELD_METHODS = ['MIG', 'TIG', 'Stick', 'Flux-Core', 'Sub-Arc', 'Spot', 'Plasma arc', 'Oxy-acetylene', 'Brazing', 'Soldering'] as const;
export type WeldMethod = typeof WELD_METHODS[number];

// Common electrodes/wires per method — extensible
export const WELD_FILLERS_BY_METHOD: Record<WeldMethod, string[]> = {
  MIG: ['ER70S-6', 'ER70S-3', 'ER80S-D2', 'ER308L', 'ER316L', 'ER309L', '4043 (Al)', '5356 (Al)'],
  TIG: ['ER70S-2', 'ER308L', 'ER316L', 'ER309L', 'EWTh-2 (thoriated)', 'EWZr (zirconiated)', 'EWP (pure tungsten)', '4043 (Al)', '5356 (Al)'],
  Stick: ['E6010', 'E6011', 'E6013', 'E7014', 'E7018', 'E308-16', 'E316-16'],
  'Flux-Core': ['E71T-1', 'E71T-11', 'E70T-1', 'E308LT', 'E309LT'],
  'Sub-Arc': ['EM12K', 'EM13K', 'EH14'],
  Spot: [],
  'Plasma arc': [],
  'Oxy-acetylene': ['RG45', 'RG60'],
  Brazing: ['BAg-1', 'BAg-7', 'BCuP-3', 'BCuP-5', 'RBCuZn-C'],
  Soldering: ['Sn63/Pb37', 'Sn96.5/Ag3.5', 'lead-free'],
};

export const WIRE_DIAMETERS = ['0.023"', '0.030"', '0.035"', '0.045"', '1/16"', '3/32"', '1/8"', '5/32"', '3/16"'] as const;
export type WireDiameter = typeof WIRE_DIAMETERS[number];
